/**
 * PWA-UPDATE-1 — a deployed update reaches an installed app with no user action.
 *
 * THE BUG THIS FIXES. `skipWaiting` and `clientsClaim` have been set for a long time, and they are
 * genuinely necessary, but they were never sufficient — and the gap is easy to miss because the
 * config reads as if the job is done. They make the NEW worker active. They do nothing about the page
 * that is already open, which keeps executing the OLD JavaScript bundle until a full page load. And
 * the entire registration was this, injected by vite-plugin-pwa:
 *
 *     window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js', {scope:'/'}))
 *
 * `load` does not fire when a phone resumes an app that was already running. So an installed PWA that
 * is opened from the app switcher rather than cold-started never asks whether a new version exists,
 * and even if the platform's own check happens to find one, nothing reloads the page to run it. That
 * is the founder's report — "I deployed and my phone still shows the old screen" — and it is not a
 * caching mystery, it is two missing listeners.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DO: register the service worker.
 * `registerSW.js` runs from a plain <script> in <head>, outside the hashed module graph. If a future
 * bundle throws at module scope, that script still runs and the worker still updates — which is the
 * only way a broken build can be replaced by a fixed one. Moving registration in here would put the
 * recovery path behind the thing that broke. So we attach to the registration that already exists and
 * return silently if there is none.
 *
 * WHY `getRegistration()` AND NOT `navigator.serviceWorker.ready`:
 * `ready` never settles when nothing is registered — it does not reject, it hangs. This codebase has
 * already been bitten by it once; see the timeout race in utils/push-subscription.ts.
 */
import * as Sentry from '@sentry/react';
import { isBusyClaimed, onBusyChange } from './busyRegistry';
import { BUILD_MARKER } from './buildMarker';

export interface SwUpdateOptions {
  /**
   * Injected, never imported. Two QueryClient instances exist in this codebase — the live one in
   * main.tsx (not exported) and a dead one in providers/QueryProvider.tsx (exported, zero importers).
   * Importing is a coin flip that fails silently: the wrong instance returns 0 forever and the guard
   * simply never fires.
   */
  isMutating: () => number;
}

export type UpdatePhase = 'idle' | 'deferred' | 'reloading';
export interface UpdateState {
  phase: UpdatePhase;
  /** True once a new worker has taken control and we owe the user a reload. */
  pending: boolean;
  /** True once the deferral has lasted long enough to be worth telling the user about. */
  chipVisible: boolean;
}

const RELOAD_BREAKER_KEY = 'aisistoo_sw_reload_at';
const RELOAD_BREAKER_MS = 60_000;
const UPDATE_INTERVAL_MS = 30 * 60_000;
const DEFER_POLL_MS = 2_000;
const CHIP_AFTER_MS = 8_000;
const LAUNCH_GRACE_MS = 3_000;
const RELOAD_NOTICE_MS = 1_200;
const MAX_ERROR_REPORTS = 3;

let state: UpdateState = { phase: 'idle', pending: false, chipVisible: false };
let reloadInFlight = false;
let deferredSince = 0;
let deferTimer: ReturnType<typeof setInterval> | null = null;
let lastInteractionAt = 0;
let visibleSince = Date.now();
let errorReports = 0;
let started = false;
let opts: SwUpdateOptions = { isMutating: () => 0 };

const subscribers = new Set<() => void>();

function setState(next: Partial<UpdateState>): void {
  const merged = { ...state, ...next };
  if (merged.phase === state.phase && merged.pending === state.pending && merged.chipVisible === state.chipVisible) {
    return;
  }
  state = merged;
  // PWAInstallPrompt already listens for both of these (it checks __aisistooUpdatePending on mount and
  // subscribes to the event) — they were wired long ago and nothing ever fired them, so the install
  // banner could stack on top of an update notice. Firing them here costs nothing and fixes that.
  try {
    (window as unknown as { __aisistooUpdatePending?: boolean }).__aisistooUpdatePending = state.pending;
    window.dispatchEvent(new CustomEvent('aisistoo:update-pending', { detail: state.pending }));
  } catch {
    /* never let notification plumbing break the update path */
  }
  for (const fn of subscribers) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

export function subscribeUpdateState(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

export function getUpdateState(): UpdateState {
  return state;
}

/**
 * Routes where an update must not even be CHECKED FOR, let alone acted on.
 *
 * The olympiad exam is the whole list. Answers there save through a bare `api.post` and any answer
 * taken while offline lives only in React state, so neither `isMutating()` nor a busy claim would
 * necessarily see them; the exam is timed and ties break on duration, so the mandatory ~2.6s splash
 * on reload is a real competitive cost. `update()` is suppressed too, not just the reload: activating
 * a new worker swaps the precache underneath a running exam.
 */
function isSuppressedRoute(): boolean {
  return window.location.pathname.startsWith('/olympiad/exam/');
}

function reportUpdateFailure(err: unknown, tag: string): void {
  if (errorReports >= MAX_ERROR_REPORTS) return;
  errorReports += 1;
  try {
    Sentry.captureException(err, { tags: { pwa: tag } });
  } catch {
    /* ignore */
  }
}

/**
 * A rejection here is the ONLY externally-visible symptom of a client that has gone permanently
 * stale — `update()` rejects on a bad-MIME /sw.js and leaves the old worker serving forever. Swallowed,
 * it is indistinguishable from "checked, nothing new", which is the exact silence this slice exists to
 * end. The user still sees nothing; the telemetry does.
 */
function checkForUpdate(reg: ServiceWorkerRegistration): void {
  if (isSuppressedRoute()) return;
  reg.update().catch((err) => reportUpdateFailure(err, 'sw-update-failed'));
}

function readBreaker(): number {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_BREAKER_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    // Storage can throw outright under blocked-cookie configurations. Degrade to the in-memory
    // guard rather than letting the handler die — a missing breaker is survivable, a dead handler
    // is the original bug.
    return 0;
  }
}

function writeBreaker(): void {
  try {
    window.sessionStorage.setItem(RELOAD_BREAKER_KEY, String(Date.now()));
  } catch {
    /* see readBreaker */
  }
}

function stopDeferTimer(): void {
  if (deferTimer !== null) {
    clearInterval(deferTimer);
    deferTimer = null;
  }
}

function startDeferTimer(): void {
  if (deferTimer !== null) return;
  deferTimer = setInterval(() => maybeReload(), DEFER_POLL_MS);
}

function doReload(): void {
  reloadInFlight = true;
  stopDeferTimer();
  writeBreaker();

  // Silent only when this reads as a launch rather than an interruption: the document became visible
  // moments ago and the user has not touched anything since. Otherwise say so first — a screen that
  // blanks and comes back unannounced reads as a crash, especially on iOS where it is a white flash.
  const silent = Date.now() - visibleSince < LAUNCH_GRACE_MS && lastInteractionAt < visibleSince;
  if (silent) {
    window.location.reload();
    return;
  }
  setState({ phase: 'reloading', chipVisible: true });
  setTimeout(() => window.location.reload(), RELOAD_NOTICE_MS);
}

function maybeReload(): void {
  if (!state.pending || reloadInFlight) return;

  // A hidden tab is not a safe moment. controllerchange fires in EVERY controlled client, so without
  // this a background tab holding a half-filled form reloads while nobody is looking at it — and the
  // notice would render to a document the user cannot see.
  if (document.hidden) return;
  if (isSuppressedRoute()) return;

  if (isBusyClaimed() || opts.isMutating() > 0) {
    startDeferTimer();
    if (deferredSince > 0 && Date.now() - deferredSince > CHIP_AFTER_MS && !state.chipVisible) {
      setState({ phase: 'deferred', chipVisible: true });
    }
    return;
  }

  // A genuine loop reloads within seconds; a second legitimate deploy is minutes or hours later. A
  // one-shot "already updated this session" key would have capped an installed PWA at ONE update for
  // the life of its browsing context — days — which is the very staleness being fixed.
  const last = readBreaker();
  if (last > 0 && Date.now() - last < RELOAD_BREAKER_MS) {
    setState({ phase: 'deferred', chipVisible: true });
    // [REQ-SEP-04 P0.3] The breaker branch used to return WITHOUT starting the defer timer, and
    // the 30-min interval only ever called checkForUpdate — so an always-foreground screen (a
    // reception desk, a wall tablet) whose reload the breaker refused sat pending-with-chip
    // FOREVER, with no retry path short of a tab-away or the chip. The timer re-drives
    // maybeReload; once the breaker window lapses the reload proceeds on its own.
    startDeferTimer();
    reportUpdateFailure(new Error('sw reload breaker engaged'), 'reload-breaker');
    return;
  }

  doReload();
}

/** The chip's "Refresh now". The user asking is consent, so this bypasses busy and the breaker. */
export function forceUpdateReload(): void {
  if (reloadInFlight) return;
  reloadInFlight = true;
  stopDeferTimer();
  writeBreaker();
  setState({ phase: 'reloading', chipVisible: true });
  setTimeout(() => window.location.reload(), RELOAD_NOTICE_MS);
}

/**
 * Get the registration, waiting for one to appear if it has not been created yet.
 *
 * THIS IS NOT DEFENSIVE PADDING — the headless probe proved it is required. `registerSW.js` calls
 * `register()` inside a `load` listener, while this module runs during module evaluation, which is
 * strictly earlier. So on any visit where nothing is registered yet, a plain `getRegistration()`
 * resolves to `undefined`, and returning there would mean NO update listeners are attached for the
 * entire life of that page: no visibilitychange check, no pageshow check, no interval. The app would
 * update on the second visit and never on the first.
 *
 * Bounded on purpose. `navigator.serviceWorker.ready` would read more naturally and is exactly the
 * wrong tool: it never settles when no registration exists, it just hangs, and this codebase has
 * already been bitten by that once (see utils/push-subscription.ts).
 */
async function acquireRegistration(): Promise<ServiceWorkerRegistration | null> {
  let reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;

  if (document.readyState !== 'complete') {
    await new Promise<void>((resolve) => {
      window.addEventListener('load', () => resolve(), { once: true });
    });
  }

  for (let i = 0; i < 10; i += 1) {
    reg = await navigator.serviceWorker.getRegistration();
    if (reg) return reg;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  return null;
}

export function initSwUpdate(options: SwUpdateOptions): void {
  if (started) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  started = true;
  opts = options;

  // Readable from the console on a real device, which is the only way to answer "is this phone on the
  // new build?" without a chunk-hash comparison in DevTools.
  try {
    (window as unknown as { __aisistooBuild?: string }).__aisistooBuild = BUILD_MARKER;
  } catch {
    /* ignore */
  }

  window.addEventListener('pointerdown', () => { lastInteractionAt = Date.now(); }, { passive: true });
  window.addEventListener('keydown', () => { lastInteractionAt = Date.now(); }, { passive: true });

  // WHY DETECTION IS NOT "COUNT controllerchange EVENTS".
  //
  // The obvious design — remember whether a controller existed at startup, then treat the first
  // controllerchange as the initial claim and every later one as an update — is what I built first,
  // and the headless probe failed it intermittently. The reason is a race, and it is not exotic:
  // this module initialises during module evaluation, while `registerSW.js` registers on `load`, so
  // at init there is very often NO controller yet. Whichever worker then claims the page first
  // occupies the "initial claim" slot. On a machine where a deploy lands in that window, the NEW
  // worker's claim is the one that gets swallowed, and the update is discarded in silence. That is
  // the exact bug this slice exists to remove, reintroduced by its own guard, and it would have been
  // invisible in production — there is no error, just a phone that stays on the old build.
  //
  // `updatefound` has no such ambiguity. It fires when a worker begins installing, and at that
  // instant `navigator.serviceWorker.controller` tells us unambiguously which case we are in:
  //   - controller === null  -> nothing was controlling this page; this is the first install, and
  //                             the page is already running the code that worker will serve. No reload.
  //   - controller !== null  -> a worker was already serving this page and a DIFFERENT one is now
  //                             installing. That is a genuine update, whatever the event ordering.
  const markUpdatePending = () => {
    if (state.pending) return;
    deferredSince = Date.now();
    setState({ pending: true, phase: 'deferred' });
    maybeReload();
  };

  const watchInstalling = (reg: ServiceWorkerRegistration) => {
    /*
     * [REQ-SEP-04 P0.3 / RC-3] A worker that finished INSTALLING while nothing was listening
     * sits in reg.waiting (briefly, with skipWaiting) — the original code looked only at
     * reg.installing, so an update that raced acquireRegistration()'s async window (or completed
     * while the page's JS was frozen in the background) was never detected: no chip, no reload,
     * for that page's entire life, while the NEW worker already controlled it and had evicted
     * the old precache. That page then died on its first never-loaded chunk.
     */
    if (reg.waiting && navigator.serviceWorker.controller !== null) {
      markUpdatePending();
      return;
    }
    const incoming = reg.installing;
    if (!incoming) return;
    if (navigator.serviceWorker.controller === null) return; // first install for an uncontrolled page
    incoming.addEventListener('statechange', () => {
      if (incoming.state !== 'activated' || state.pending) return;
      markUpdatePending();
    });
  };

  acquireRegistration()
    .then((reg) => {
      if (!reg) return;

      reg.addEventListener('updatefound', () => watchInstalling(reg));
      // A worker may already be mid-install by the time acquisition finishes — that updatefound has
      // then already fired and we would never hear about it.
      watchInstalling(reg);

      /*
       * [REQ-SEP-04 P0.3 / RC-3] THE controllerchange BACKSTOP — deliberately a backstop, not the
       * primary detector. The :278-295 reasoning against controllerchange stands: at module init
       * there is often NO controller, so "count the events" cannot tell a first claim from an
       * update. This listener sidesteps that ambiguity entirely by only ACTING when a controller
       * already existed before the event fired — a first install on an uncontrolled page cannot
       * satisfy that, so every triggering event is unambiguously a live-page takeover the
       * install-watchers missed (an install that completed while the page's JS was frozen, or one
       * that raced the acquisition window above).
       */
      let hadController = navigator.serviceWorker.controller !== null;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (hadController) markUpdatePending();
        hadController = true;
      });

      /*
       * [REQ-SEP-04 P0.3 / RC-3] THE BUILD-SKEW CHECK — the state-based net under the two
       * event-based detectors above. Events can be lost wholesale: a phone freezes the page's JS,
       * the browser updates + activates the new worker on its own, and on resume there is no
       * installing worker, no waiting worker, and (on some engines) no replayed controllerchange —
       * every listener stays silent while the page runs OLD code against the NEW worker's precache.
       * States cannot be lost: fetch index.html (no-store — though the SW's precache answers it,
       * which is the point: the ACTIVE worker's copy) and compare its entry-chunk src against the
       * one THIS document booted from. A mismatch means the running page is provably not the build
       * the worker now serves — mark pending and let maybeReload's guards decide when. Throttled;
       * failures are swallowed (a flaky fetch must not invent an update).
       */
      let lastSkewCheckAt = 0;
      const SKEW_CHECK_MIN_GAP_MS = 20_000;
      const ownEntrySrc = (): string | null => {
        const s = document.querySelector('script[type="module"][src*="/assets/index-"]');
        return s ? new URL(s.getAttribute('src') as string, window.location.origin).pathname : null;
      };
      const checkBuildSkew = (): void => {
        if (state.pending || isSuppressedRoute()) return;
        const now = Date.now();
        if (now - lastSkewCheckAt < SKEW_CHECK_MIN_GAP_MS) return;
        lastSkewCheckAt = now;
        const mine = ownEntrySrc();
        if (!mine) return;
        fetch('/index.html', { cache: 'no-store' })
          .then((r) => (r.ok ? r.text() : null))
          .then((html) => {
            if (!html) return;
            const m = html.match(/src="(\/assets\/index-[A-Za-z0-9_-]+\.js)"/);
            if (m && m[1] !== mine) markUpdatePending();
          })
          .catch(() => { /* offline or blocked — never invent an update from a failed fetch */ });
      };

      checkForUpdate(reg);

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) return;
        visibleSince = Date.now();
        checkForUpdate(reg);
        checkBuildSkew(); // [P0.3] the missed-event net — see above
        maybeReload();
      });

      // Covers a restore from the back/forward cache, where no fresh load event fires.
      window.addEventListener('pageshow', () => { checkForUpdate(reg); checkBuildSkew(); });

      // The only discovery path for a screen that is never backgrounded — a reception desk or a wall
      // tablet left open all day fires no visibility event from one deploy to the next.
      // [REQ-SEP-04 P0.3] It now also re-drives maybeReload, so a pending update whose reload was
      // deferred (busy, breaker) is retried here rather than only on a visibility flip.
      setInterval(() => {
        if (!document.hidden) {
          checkForUpdate(reg);
          maybeReload();
        }
      }, UPDATE_INTERVAL_MS);

      onBusyChange(() => maybeReload());

      // Signals that update discovery is LIVE — the registration is in hand and every listener is
      // attached. Acquisition is asynchronous (see acquireRegistration), so there is a window after
      // the worker has claimed the page during which nothing is listening yet. The headless probe
      // waits on this rather than sleeping and hoping; without it the probe raced the app and
      // reported failures that were its own timing.
      try {
        (window as unknown as { __aisistooSwReady?: boolean }).__aisistooSwReady = true;
      } catch {
        /* ignore */
      }
    })
    .catch((err) => reportUpdateFailure(err, 'sw-getregistration-failed'));
}
